/**
 * The rules behind "+ Add deal".
 *
 * ── Why this file exists ───────────────────────────────────────────────────
 *
 * Until now the board's add-deal box asked for a name and nothing else, so
 * every deal created there was attached to nobody. On 1 Sep 2026 that had cost
 * 23 orphan deals — 18 of them in Partnerships — two of which were closed and
 * therefore invisible on the Leads tab: a real Lost that no screen could show.
 *
 * The obvious fix, "make Promote the only way in", was rejected and rightly.
 * Not every deal starts as a lead — a renewal, a referral that arrives as a
 * company, an existing client's second order. Force everything through Promote
 * and somebody with a real deal and no lead invents a lead to get past the
 * gate. Then the Leads table holds rows that were never leads and every number
 * on it is quietly wrong. An unattached deal is a visible gap; a fake lead is
 * an invisible one.
 *
 * So the box does not ask for a name. It asks who the deal is for, and takes
 * the name from the answer.
 *
 * Kyu, 1 Sep 2026: "Type 'Lazzara', pick, done. Fewer keystrokes than today
 * for the normal case."
 *
 * Pure functions only — no React, no Supabase. The component that draws this
 * is AddDealRow.tsx and it should hold no rules of its own.
 */

export interface LeadLike {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  company_id: string | null;
}

export interface CompanyLike {
  id: string;
  name: string;
}

/** A row offered under the search box. */
export interface AddDealChoice {
  id: string;
  /** What the row reads as: "Phil Lonsdale — Cole & Van Note". */
  label: string;
  /** What the deal would be called if this row is picked. */
  dealName: string;
}

/**
 * At most this many rows are offered.
 *
 * Not a performance number — 475 leads filter instantly. It is a reading
 * number: a list long enough to scroll is a list you search again rather than
 * read, and the point of this box is to end the interaction in one glance.
 */
export const MAX_CHOICES = 6;

/**
 * A deal is named after the company, not the person.
 *
 * Every deal already on the board is named this way — "Cole & Van Note",
 * "Lazzara Orthodontics", "Carolina Skiff - 2026". Naming them after the
 * contact would make the board unreadable at a glance and would look wrong
 * the day the contact changes, which on Partnerships is often. The person is
 * still attached; they are just not the title.
 *
 * Falls back to the person's name, then the email, so a lead with no company
 * still produces something a human can recognise instead of an empty title.
 */
export function dealNameForLead(lead: LeadLike): string {
  const company = lead.company_name.trim();
  if (company !== '') return company;
  const person = `${lead.first_name} ${lead.last_name}`.trim();
  if (person !== '') return person;
  const email = lead.email.trim();
  return email === '' ? 'Untitled deal' : email;
}

/** The row text: who they are, and where they work. */
export function leadChoiceLabel(lead: LeadLike): string {
  const person = `${lead.first_name} ${lead.last_name}`.trim();
  const company = lead.company_name.trim();
  if (person === '') return company === '' ? lead.email.trim() : company;
  return company === '' ? person : `${person} — ${company}`;
}

/**
 * Which leads match what has been typed.
 *
 * Matching is on the whole row text — name, company and email together —
 * because people search by whichever of the three they happen to remember.
 * Typing "lazzara" finds Andrea Brigman; typing "andrea" finds her too.
 *
 * An empty query returns NOTHING, deliberately. Offering the first six leads
 * alphabetically to somebody who has not typed yet invites a mis-click that
 * attaches a real deal to a stranger, and a wrong attachment is worse than no
 * attachment because it looks finished.
 */
export function matchLeads(leads: LeadLike[], query: string): AddDealChoice[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const matches: AddDealChoice[] = [];
  for (const lead of leads) {
    const label = leadChoiceLabel(lead);
    const haystack = `${label} ${lead.email}`.toLowerCase();
    if (haystack.includes(needle)) {
      matches.push({ id: lead.id, label, dealName: dealNameForLead(lead) });
    }
    if (matches.length === MAX_CHOICES) break;
  }
  return matches;
}

/** The same, for the "not a lead" case. Companies have only one field to match. */
export function matchCompanies(companies: CompanyLike[], query: string): AddDealChoice[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const matches: AddDealChoice[] = [];
  for (const company of companies) {
    if (company.name.toLowerCase().includes(needle)) {
      matches.push({ id: company.id, label: company.name, dealName: company.name });
    }
    if (matches.length === MAX_CHOICES) break;
  }
  return matches;
}

/**
 * What the write should be, once a row is picked.
 *
 * Picking a LEAD carries that lead's company across as well, exactly as
 * promote_lead_to_deal does. That inheritance is the whole reason a deal ever
 * reaches a company profile, and leaving it out is how the 1 Sep cleanup ended
 * up needing a second pass for six deals whose contact had a company the deal
 * never got.
 *
 * Picking a COMPANY leaves the contact null on purpose. That is the renewal
 * and referral case, and a deal with a company and no contact is a legitimate
 * answer rather than a half-finished one.
 */
export function writeForChoice(
  kind: 'lead' | 'company',
  choice: AddDealChoice,
  leads: LeadLike[],
): { name: string; leadId: string | null; companyId: string | null } {
  if (kind === 'company') {
    return { name: choice.dealName, leadId: null, companyId: choice.id };
  }
  const lead = leads.find((row) => row.id === choice.id);
  return {
    name: choice.dealName,
    leadId: choice.id,
    companyId: lead?.company_id ?? null,
  };
}
